import {
    createContext,
    forwardRef,
    Fragment,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react';
import { type AriaRadioProps, useRadioGroup } from 'react-aria';
import { type RadioGroupProps, type RadioGroupState, useRadioGroupState } from 'react-stately';
import { VStack } from '@direct-frontend/components';
import { type Nullable } from '@direct-frontend/stdlib';
import { useFormInstance, useFormValue } from '@adv-frontend/form-kit';
import { OrderDeliveryMethods, OrderDeliveryServices, type ServiceType } from '@/shared/api/order';
import { ServiceTypeIcon } from '@/shared/components/ServiceTypeIcon';
import { Slot, SlotCheckbox } from '@/shared/components/Slot';
import { formatTimestampInterval } from '@/shared/lib/datetime';
import { formatPrice } from '@/shared/lib/formatPrice';
import { useRadioState } from '@/shared/lib/hooks';
import { isElementInParentViewport } from '@/shared/lib/isElementInParentViewport';
import { type OzonDeliveryInterval } from '../../../../../lib';
import { useSlotListScrollHeight } from '../../lib/useSlotListScrollHeight';
import { deliveryIntervalForm } from '../model/deliveryIntervalForm';
import styles from './TimeSlotsRadioGroup.module.css';

const RadioContext = createContext<RadioGroupState | null>(null);

interface IntervalOptionProps extends AriaRadioProps {
    title: string;
    serviceType: ServiceType;
    price: Nullable<string>;
    currency: Nullable<string>;
    deliveryMethod?: Nullable<string>;
    border: boolean;
    ozonDeliveryInterval?: OzonDeliveryInterval;
}

const IntervalOption = forwardRef<HTMLDivElement, IntervalOptionProps>(function IntervalOption(props, forwardedRef) {
    const { title, serviceType, price, currency, deliveryMethod, border, value, ozonDeliveryInterval } = props;

    const ref = useRef<HTMLDivElement>(null);
    const state = useRadioState(RadioContext);
    const isCourierDelivery = deliveryMethod === OrderDeliveryMethods.COURIER;

    const subtitle = useMemo(() => {
        if (price && currency && isCourierDelivery) {
            return `${parseFloat(price) === 0 ? 'Бесплатно' : formatPrice(price, currency)}`;
        }

        return '';
    }, [currency, isCourierDelivery, price]);

    useImperativeHandle(forwardedRef, () => ref.current as HTMLDivElement);

    useEffect(() => {
        if (state.selectedValue !== value) {
            return;
        }

        const node = ref.current;

        /** Без requestAnimationFrame не скроллит */
        requestAnimationFrame(() => {
            if (node && !isElementInParentViewport(node)) {
                node.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        });
    }, [state.selectedValue, value]);

    if (serviceType === OrderDeliveryServices.OZON && ozonDeliveryInterval?.IsInvalidOzonUser) {
        const { getSubtitle, registrationButton } = ozonDeliveryInterval;

        return (
            <VStack ref={ref}>
                <Slot
                    className={styles.option}
                    title={title}
                    subtitle={getSubtitle(subtitle)}
                    border={border}
                    icon={<ServiceTypeIcon serviceType={serviceType} size="24" />}
                    addonRight={registrationButton}
                    disabled
                />
            </VStack>
        );
    }

    return (
        <SlotCheckbox
            ref={ref}
            className={styles.option}
            title={title}
            subtitle={subtitle}
            border={border}
            icon={<ServiceTypeIcon serviceType={serviceType} size="24" />}
            checkboxState={state}
            value={value}
        />
    );
});

type Interval = {
    from: number;
    to: number;
    serviceType: ServiceType;
    price: Nullable<string>;
    currency: Nullable<string>;
};

interface TimeSlotsRadioGroupProps {
    formId: string;
    intervals: Interval[];
    deliveryMethod?: Nullable<string>;
    maxIntervals?: number;
    ozonDeliveryInterval?: OzonDeliveryInterval;
}

export const TimeSlotsRadioGroup = (props: TimeSlotsRadioGroupProps) => {
    const { formId, intervals, maxIntervals, deliveryMethod, ozonDeliveryInterval } = props;

    const { fields } = useFormInstance({ form: deliveryIntervalForm, id: formId });
    const value = useFormValue(deliveryIntervalForm, formId);

    const { wrapperHeight, labelsRef } = useSlotListScrollHeight(intervals.length, maxIntervals);

    const options = useMemo(
        () =>
            intervals.reduce(
                (
                    acc: Record<
                        string,
                        {
                            title: string;
                            interval: Interval;
                            serviceType: ServiceType;
                            price: Nullable<string>;
                            currency: Nullable<string>;
                        }
                    >,
                    interval
                ) => {
                    const showTimeRange = deliveryMethod === OrderDeliveryMethods.COURIER;
                    const showMultiDayRange = deliveryMethod === OrderDeliveryMethods.COURIER;

                    acc[`${interval.from}-${interval.to}-${interval.serviceType}`] = {
                        title: formatTimestampInterval(
                            { from: interval.from, to: interval.to },
                            { style: 'short', showTimeRange, showMultiDayRange }
                        ),
                        interval,
                        serviceType: interval.serviceType,
                        price: interval.price,
                        currency: interval.currency,
                    };

                    return acc;
                },
                {}
            ),
        [deliveryMethod, intervals]
    );

    const onIntervalChange = useCallback(
        (interval: string) => {
            fields.deliveryInterval.setValue({ type: 'interval', ...options[interval].interval });
        },
        [fields.deliveryInterval, options]
    );

    const dateRadioProps: RadioGroupProps = {
        orientation: 'vertical',
        value:
            value.deliveryInterval.type === 'interval'
                ? `${value.deliveryInterval.from}-${value.deliveryInterval.to}-${value.deliveryInterval.serviceType}`
                : null,
        onChange: onIntervalChange,
    };
    const radioGroupState = useRadioGroupState(dateRadioProps);

    const { radioGroupProps } = useRadioGroup(dateRadioProps, radioGroupState);

    return (
        <VStack
            gap="1"
            style={{ '--ys-scroll-height': Boolean(wrapperHeight) && `${wrapperHeight}px` }}
            {...radioGroupProps}
            className={styles.root}
        >
            <RadioContext.Provider value={radioGroupState}>
                {Object.keys(options).map((key, index) => {
                    const serviceType = options[key].serviceType;

                    const ozonDeliveryIntervalProp =
                        serviceType === OrderDeliveryServices.OZON ? ozonDeliveryInterval : undefined;

                    return (
                        <Fragment key={key}>
                            <IntervalOption
                                ref={(ref) => {
                                    if (ref) {
                                        labelsRef.current[index] = ref;
                                    }
                                }}
                                aria-label={key}
                                value={key}
                                title={options[key].title}
                                serviceType={serviceType}
                                price={options[key].interval.price}
                                currency={options[key].interval.currency}
                                deliveryMethod={deliveryMethod}
                                border={index < Object.keys(options).length - 1}
                                ozonDeliveryInterval={ozonDeliveryIntervalProp}
                            />
                        </Fragment>
                    );
                })}
            </RadioContext.Provider>
        </VStack>
    );
};
